import { useEffect, useMemo } from "react"

import { ImdfFeature } from "providers/venue/types"
import { featureCollection, point, polygon } from "@turf/helpers"
import transformScale from "@turf/transform-scale"
import union from "@turf/union"
import booleanPointInPolygon from "@turf/boolean-point-in-polygon"
import pointOnFeature from "@turf/point-on-feature"

import { useVenue } from "providers/venue/useVenue"
import { useIndoorMap } from "./useIndoorMap"
import debounce from "lodash/debounce"
import { Position } from "geojson"
import { isValidPoint } from "utils/geometry"

const DEFAULT_OPTIONS = {
  scale: 1.25,
}

const MOVE_END_DELAY_MS = 200

type Options = {
  scale?: number
}
type UseLimitMapView = (features: ImdfFeature[], options: Options) => void

export const useLimitMapView: UseLimitMapView = (
  features: ImdfFeature[] = [],
  options = {}
) => {
  const { indoorRef, mapLoaded } = useIndoorMap()
  const { dataLoaded } = useVenue()
  const { scale = DEFAULT_OPTIONS.scale } = useMemo(
    () => ({ ...DEFAULT_OPTIONS, ...options }),
    [options]
  )

  const mapReady = indoorRef.current && mapLoaded && dataLoaded

  useEffect(() => {
    const indoorMap = indoorRef.current

    try {
      if (indoorMap && mapReady && scale !== 0) {
        const scaledFeatures = features.map((feature) =>
          transformScale(feature, scale)
        )

        const boundaryPolygons = scaledFeatures.map((feature) =>
          polygon(feature.geometry.coordinates as Position[][])
        )

        const polygonBoundary = union(featureCollection(boundaryPolygons))

        const boundaryFallbackPoint = pointOnFeature(polygonBoundary)
        const boundaryFallbackCoordinate = boundaryFallbackPoint.geometry
          .coordinates as [number, number]

        const handleBoundaryOnMoveEnd = () => {
          // Skip while we're mid-flight on our own programmatic fly (e.g. place-detail
          // focus) — otherwise this fires off the fly's own moveend and yanks the view
          // toward the venue boundary before the intended fly completes.
          if (indoorMap.isProgrammaticFlying()) return

          const mapCenter = indoorMap.map.getCenter()
          const isCenterInsideBoundary = booleanPointInPolygon(
            point([mapCenter.x, mapCenter.y]),
            polygonBoundary
          )

          if (!isCenterInsideBoundary) {
            const nearestVenueInView = indoorMap.findVenueInView()?.venue
            const nearestVenueDisplayPoint =
              nearestVenueInView?.properties?.display_point

            const recenterCoordinate = isValidPoint(nearestVenueDisplayPoint)
              ? nearestVenueDisplayPoint.coordinates
              : boundaryFallbackCoordinate

            indoorMap.flyTo(recenterCoordinate as any, {
              zoom: indoorMap.map.getZoom(),
              bearing: indoorMap.map.getBearing(),
              pitch: indoorMap.map.getPitch(),
              duration: 450,
              delay: 0,
              ease: "out",
            })
          }
        }

        const debouncedBoundaryOnMoveEnd = debounce(
          handleBoundaryOnMoveEnd,
          MOVE_END_DELAY_MS
        )

        indoorMap.map.on("moveend", debouncedBoundaryOnMoveEnd)

        return () => {
          indoorMap.map.off("moveend", debouncedBoundaryOnMoveEnd)
          debouncedBoundaryOnMoveEnd.cancel()
        }
      }
    } catch (error) {
      console.log(`error:unable to limit map view`)
    }
  }, [mapReady, indoorRef, features, scale])
}
